import React from "react";
import ActionIcon from "@components/ui/game/action-icon/ActionIcon";
import UnitIcon from "@components/game/objects/UnitIcon";
import PlannedActionDelete from "./PlannedActionDelete";
import HoverClickPopup from "@components/ui/general/HoverClickPopup";
import useGameStore from '@store/useGameStore';
import useCinematicStore from '@store/useCinematicStore';
import useSelectionStore from '@store/useSelectionStore';
import { ClickDTO } from '@models/ClickDTO';
import { GameStatus, Team, ClickableType } from '@utils/enums';
import "./PlannedActionIcon.css";

/**
 * ✅ REFACTORIZADO - Icono de acción planificada con eliminación
 * Ahora con gestión dinámica de pointer-events según el modo de interacción
 */
export default function PlannedActionIcon({
  actions,
  interactionMode,
  onDeleteAction
}) {
  // Constants for delete button sizes
  const DELETE_BUTTON_SIZE_TOOLTIP = 16;
  const DELETE_BUTTON_SIZE_SINGLE = 16;

  // ✅ Move hooks before any early returns
  const game = useGameStore((state) => state.game);
  const { currentStep } = useCinematicStore();
  const currentStepIndex = currentStep; // Legacy compatibility
  const { handleClick } = useSelectionStore();

  // Check if we're in cinematic mode
  const isCinematicMode = game?.status === GameStatus.RESOLVING || game?.status === GameStatus.RESOLVED;

  if (!actions || actions.length === 0) {
    console.warn("PlannedActionIcon: no actions received");
    return null;
  }

  //console.info("[PlannedActionIcon] 🎯 Renderizando icono para", actions);
  //console.info("[PlannedActionIcon] 🖱️ Modo de interacción:", interactionMode);

  // Agrupar por tipo de acción
  const actionTypesMap = actions.reduce((acc, action) => {
    const key = action.action?.slug ?? "unknown";
    acc[key] = acc[key] || [];
    acc[key].push(action);
    return acc;
  }, {});

  const actionTypes = Object.keys(actionTypesMap);
  const isMixed = actionTypes.length > 1;
  const totalCount = actions.length;
  const firstAction = actions[0];
  
  const isCinematicPhase = game?.status === GameStatus.RESOLVING || game?.status === GameStatus.RESOLVED;
  
  // Check if any actions are completed during cinematic
  const completedActions = isCinematicPhase && game?.resolution_queue ? 
    actions.filter(action => {
      const actionIndex = game.resolution_queue.findIndex(event => event.id === action.id);
      return actionIndex >= 0 && actionIndex <= currentStepIndex;
    }) : [];
  
  const hasCompletedActions = completedActions.length > 0;
  const allActionsCompleted = completedActions.length === actions.length;

  // ✅ Determinar equipos mixtos ANTES de usar la variable
  const teamCounts = actions.reduce((acc, action) => {
    const team = action.teamClass || 'unknown';
    acc[team] = (acc[team] || 0) + 1;
    return acc;
  }, {});
  
  const teamKeys = Object.keys(teamCounts);
  const isMixedTeams = teamKeys.length > 1;
  
  // ✅ Si hay equipos mixtos, usar 'mixed', sino usar el equipo predominante
  const teamClass = isMixedTeams ? Team.MIXED : (firstAction?.teamClass || 'unknown');

  // Determinar qué mostrar
  const shouldShowTooltip = totalCount > 1;
  const shouldShowSingleDelete = totalCount === 1 && firstAction?.teamClass === Team.OWN;
  const shouldShowSequence = false; // Hidden - now showing initiative on units instead
  
  // Validate sequence numbers exist
  actions.forEach(action => {
    if (!action.resolution_order && !action.sequence_number) {
      console.warn('[PlannedActionIcon] Missing sequence data for action:', {
        id: action.id,
        name: action.action?.name || 'Unknown Action',
        slug: action.action?.slug || 'unknown',
        fullAction: action // Debug: show full action object
      });
    }
  });

  // Determinar icono y contador
  let displayIcon;
  let displayCount = totalCount;

  if (isMixed) {
    // Múltiples tipos diferentes: mostrar "M" con color según equipos
    displayIcon = (
      <span className={`mixed-action-indicator ${isMixedTeams ? 'mixed-teams' : ''}`}>M</span>
    );
  } else {
    // Un solo tipo: mostrar icono de la acción
    displayIcon = (
      <ActionIcon 
        action={firstAction.action}
        size={16}
        showFallback={true}
        teamClass={teamClass === Team.MIXED ? null : teamClass} // ✅ Pasar equipo al icono
      />
    );
  }


  // Manejar eliminación de acción
  const handleDeleteAction = async (actionId) => {
    //console.info("[PlannedActionIcon] 🗑️ Eliminando acción:", actionId);
    
    try {
      if (onDeleteAction) {
        await onDeleteAction(actionId);
      }
    } catch {
      //console.error("[PlannedActionIcon] ❌ Error eliminando acción");
    }
  };

  // Crear contenido del tooltip para múltiples acciones
  const tooltipContent = shouldShowTooltip ? (
    <div className="multiple-actions-tooltip">
      <h4 className="tooltip-title">Actions on this tile:</h4>
      
      <div className="actions-grid">
        {actions.map((action) => (
          <div key={action.id} className="action-row">
            <div className="action-unit-info">
              <UnitIcon 
                unitId={action.unit_id} 
                size="extra-small"
              />
              <span className="unit-name">
                {action.unit?.unit_template?.name || `Unit ${action.unit_id}`}
              </span>
            </div>
            
            <div className="action-info">
              <ActionIcon 
                action={action.action}
                size={20}
                showFallback={true}
                teamClass={action.teamClass}
              />
              <span className="action-name">
                {action.action?.name || 'Unknown Action'}
              </span>
            </div>
            
            <div className="action-sequence">
              #{(() => {
                const seq = action.resolution_order || action.sequence_number;
                if (!seq) {
                  console.warn('[PlannedActionIcon] Missing sequence number for action:', {
                    id: action.id,
                    name: action.action?.name || 'Unknown Action',
                    slug: action.action?.slug || 'unknown'
                  });
                  return '?';
                }
                return seq;
              })()}
            </div>
            {!isCinematicMode && action.teamClass === Team.OWN && (
              <div className="action-delete">
                <PlannedActionDelete
                  action={action}
                  onDelete={() => handleDeleteAction(action.id)}
                  size={DELETE_BUTTON_SIZE_TOOLTIP}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  ) : null;

  // Always position sequence display at top-left for consistency

  // Clases CSS dinámicas
  const iconClasses = [
    'planned-action-icon',
    `team-${teamClass}`, // ✅ Agregar clase de equipo para colores
    isMixed ? 'mixed-actions' : 'single-type-actions',
    `action-count-${totalCount}`,
    shouldShowTooltip ? 'has-tooltip multiple-actions' : '', // ✅ Agregar multiple-actions cuando hay tooltip
    shouldShowSingleDelete ? 'has-single-delete single-action' : '', // ✅ Agregar single-action cuando hay una sola
    shouldShowSequence ? `has-sequence sequence-top-left` : '',
    interactionMode === 'targeting' ? 'targeting-mode' : '',
    interactionMode === 'select' ? 'select-mode' : '',
    hasCompletedActions ? 'has-completed-actions' : '',
    allActionsCompleted ? 'all-completed' : '',
    isCinematicPhase ? 'cinematic-mode' : ''
  ].filter(Boolean).join(' ');

  return (
    <div className={iconClasses}>
      {/* Sequence number display - single circle or vertical stack for multiple */}
      {shouldShowSequence && totalCount === 1 && (
        <div className="sequence-display sequence-circle sequence-top-left">
          <span className="sequence-number">
            {(() => {
              const seq = firstAction.resolution_order || firstAction.sequence_number;
              if (!seq) {
                console.warn('[PlannedActionIcon] Missing sequence number for action:', {
                  id: firstAction.id,
                  name: firstAction.action?.name || 'Unknown Action',
                  slug: firstAction.action?.slug || 'unknown'
                });
                return '?';
              }
              return seq;
            })()}
          </span>
        </div>
      )}

      {/* Multiple sequence numbers - vertical stack */}
      {shouldShowSequence && totalCount > 1 && (
        <div className="sequence-stack">
          {[...new Set(actions
            .map(a => {
              const seq = a.resolution_order || a.sequence_number;
              if (!seq) {
                console.warn('[PlannedActionIcon] Missing sequence number for action:', {
                  id: a.id,
                  name: a.action?.name || 'Unknown Action',
                  slug: a.action?.slug || 'unknown'
                });
                return null;
              }
              return seq;
            })
            .filter(seq => seq !== null))]
            .sort((a, b) => a - b)
            .map((seq, index) => (
              <div
                key={seq}
                className="sequence-display sequence-circle"
                style={{ top: `${-17 + (index * 18)}px` }}
              >
                <span className="sequence-number">{seq}</span>
              </div>
            ))
          }
        </div>
      )}
      
      {/* Icono principal - Click now selects actions for panel */}
      <div
        className="action-icon-container"
        onClick={() => {
          if (!isCinematicMode && interactionMode === 'select') {
            const clickDTO = new ClickDTO(
              ClickableType.PLANNED_ACTION,
              actions,
              null,
              { gameId: game?.id }
            );
            handleClick(clickDTO);
          }
        }}
      >
        {displayIcon}
        {/* Integrated count inside */}
        {displayCount > 1 && (
          <span className="action-count-text">×{displayCount}</span>
        )}
      </div>

      {/* X para eliminar acción única */}
      {shouldShowSingleDelete && (
        <PlannedActionDelete
          action={firstAction}
          onDelete={() => handleDeleteAction(firstAction.id)}
          size={DELETE_BUTTON_SIZE_SINGLE}
        />
      )}
    </div>
  );
}

